'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { persistUtms } from '../useUtms';
import { setParentWindowURL } from '../useParentWindowURL';

type Message = {
    id?: 'cademy-iframe';
    search?: '' | `?${string}`;
    parentWindowUrl?: `http${string}`;
    iframeSrc?: string;
    message?: 'parent-url-changed';
    parentPageUtms?: Record<string, string>;
};

export const useEmbedResumeability = () => {
    const path = usePathname();
    const searchParams = useSearchParams();
    useEmbedResumeabilityListener();

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.parent === 'undefined') {
            return;
        }
        if (!path) {
            return;
        }

        const message = {
            messageType: 'navigate',
            path: path.replace('/embed/', ''),
            search: searchParams?.toString(),
        };

        window.parent.postMessage(message, '*');
    }, [path, searchParams]);
};

export const useEmbedResumeabilityListener = () => {
    const router = useRouter();
    const callback = useCallback(
        ({ data }: MessageEvent<any>): void => {
            if (!data || typeof data !== 'string') {
                return;
            }

            try {
                const payload: Message = JSON.parse(data);

                if (!payload.id || payload.id !== 'cademy-iframe') {
                    return;
                }

                const params = new URLSearchParams(payload.search);
                const cadpath = params.get('cadpath');
                params.delete('cadpath');
                const otherParams = params.toString();
                const destination =
                    cadpath + (otherParams && otherParams !== '' ? `?${otherParams}` : '');

                if (cadpath) {
                    router.push(`/embed/${destination}`);
                }

                if (payload.parentPageUtms) {
                    persistUtms(payload.parentPageUtms ?? {});
                }

                if (payload.parentWindowUrl) {
                    setParentWindowURL(payload.parentWindowUrl);
                }
            } catch (err) {
                console.error(err, data);
            }
        },
        [router]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.addEventListener('message', callback);
        return () => window.removeEventListener('message', callback);
    }, [callback]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.parent.postMessage({ messageType: 'initialised' }, '*');
    }, []);
};
